import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  MenuItem,
  Divider,
  Skeleton,
  Chip
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Description as DescriptionIcon,
  ContactPhone as ContactIcon,
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import useOrganizationSetupStore from '@/store/OrganizationSetupStore';
import { OrganizationDetails } from '@/services/OrganizationSetupService';

interface CompanyProfileSectionProps {
  canEdit: boolean;
}

const companyTypes = [
  'Private Limited',
  'Public Limited',
  'LLP',
  'Sole Proprietorship',
  'Partnership',
  'One Person Company',
  'Section 8 Company',
  'Other'
];

const InfoCard: React.FC<{
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, icon, children }) => {
  const theme = useTheme();

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        transition: 'box-shadow 0.3s ease',
        '&:hover': {
          boxShadow: theme.shadows[4]
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              backgroundColor: theme.palette.primary.main + '15',
              color: theme.palette.primary.main,
              mr: 2
            }}
          >
            {icon}
          </Box>
          <Typography variant="h6" fontWeight={600}>
            {title}
          </Typography>
        </Box>
        {children}
      </CardContent>
    </Card>
  );
};

const InfoRow: React.FC<{
  label: string;
  value?: string | null;
  loading?: boolean;
}> = ({ label, value, loading }) => {
  if (loading) {
    return (
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Skeleton variant="text" width="60%" />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body1" fontWeight={500}>
        {value || '-'}
      </Typography>
    </Box>
  );
};

const CompanyProfileSection: React.FC<CompanyProfileSectionProps> = ({ canEdit }) => {
  const theme = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<Partial<OrganizationDetails>>({});

  const {
    organizationDetails,
    detailsLoading,
    loading,
    saveOrganizationDetails
  } = useOrganizationSetupStore();

  useEffect(() => {
    if (organizationDetails) {
      setFormData(organizationDetails);
    }
  }, [organizationDetails]);

  const handleChange = (field: keyof OrganizationDetails) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleDateChange = (date: dayjs.Dayjs | null) => {
    setFormData(prev => ({
      ...prev,
      founded_date: date ? date.format('YYYY-MM-DD') : undefined
    }));
  };

  const handleSave = async () => {
    const success = await saveOrganizationDetails(formData);
    if (success) {
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setFormData(organizationDetails || {});
    setIsEditing(false);
  };

  // View Mode
  if (!isEditing) {
    return (
      <Box>
        {/* Edit Button */}
        {canEdit && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => setIsEditing(true)}
              sx={{ borderRadius: 2 }}
            >
              Edit Details
            </Button>
          </Box>
        )}

        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid size={{ xs: 12, md: 6 }}>
            <InfoCard title="Basic Information" icon={<BusinessIcon />}>
              <InfoRow
                label="Company Name"
                value={organizationDetails?.company_name}
                loading={detailsLoading}
              />
              <InfoRow
                label="Tagline"
                value={organizationDetails?.tagline}
                loading={detailsLoading}
              />
              <InfoRow
                label="Company Type"
                value={organizationDetails?.company_type}
                loading={detailsLoading}
              />
              <InfoRow
                label="Industry Sector"
                value={organizationDetails?.industry_sector}
                loading={detailsLoading}
              />
              <InfoRow
                label="Founded"
                value={organizationDetails?.founded_date
                  ? dayjs(organizationDetails.founded_date).format('MMMM D, YYYY')
                  : null}
                loading={detailsLoading}
              />
            </InfoCard>
          </Grid>

          {/* Registration Details */}
          <Grid size={{ xs: 12, md: 6 }}>
            <InfoCard title="Registration Details" icon={<DescriptionIcon />}>
              <InfoRow
                label="GST Number"
                value={organizationDetails?.gst_number}
                loading={detailsLoading}
              />
              <InfoRow
                label="Registration Number (CIN)"
                value={organizationDetails?.registration_number}
                loading={detailsLoading}
              />
              <InfoRow
                label="PAN Number"
                value={organizationDetails?.pan_number}
                loading={detailsLoading}
              />
            </InfoCard>
          </Grid>

          {/* Address */}
          <Grid size={{ xs: 12, md: 6 }}>
            <InfoCard title="Address" icon={<LocationIcon />}>
              <InfoRow
                label="Address Line 1"
                value={organizationDetails?.address_line1}
                loading={detailsLoading}
              />
              <InfoRow
                label="Address Line 2"
                value={organizationDetails?.address_line2}
                loading={detailsLoading}
              />
              <Box sx={{ display: 'flex', gap: 4 }}>
                <InfoRow
                  label="City"
                  value={organizationDetails?.city}
                  loading={detailsLoading}
                />
                <InfoRow
                  label="State"
                  value={organizationDetails?.state}
                  loading={detailsLoading}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 4 }}>
                <InfoRow
                  label="Country"
                  value={organizationDetails?.country}
                  loading={detailsLoading}
                />
                <InfoRow
                  label="Pincode"
                  value={organizationDetails?.pincode}
                  loading={detailsLoading}
                />
              </Box>
            </InfoCard>
          </Grid>

          {/* Contact Information */}
          <Grid size={{ xs: 12, md: 6 }}>
            <InfoCard title="Contact Information" icon={<ContactIcon />}>
              <InfoRow
                label="Email"
                value={organizationDetails?.email}
                loading={detailsLoading}
              />
              <InfoRow
                label="Phone"
                value={organizationDetails?.phone}
                loading={detailsLoading}
              />
              <InfoRow
                label="Alternate Phone"
                value={organizationDetails?.alternate_phone}
                loading={detailsLoading}
              />
              <Divider sx={{ my: 2 }} />
              <InfoRow
                label="Website"
                value={organizationDetails?.website}
                loading={detailsLoading}
              />
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                {organizationDetails?.linkedin_url && (
                  <Chip
                    icon={<LinkedInIcon />}
                    label="LinkedIn"
                    component="a"
                    href={organizationDetails.linkedin_url}
                    target="_blank"
                    clickable
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
                {organizationDetails?.twitter_url && (
                  <Chip
                    icon={<TwitterIcon />}
                    label="Twitter"
                    component="a"
                    href={organizationDetails.twitter_url}
                    target="_blank"
                    clickable
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>
            </InfoCard>
          </Grid>
        </Grid>
      </Box>
    );
  }

  // Edit Mode
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        {/* Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3, gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<CloseIcon />}
            onClick={handleCancel}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={loading}
            sx={{ borderRadius: 2 }}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>

        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
            p: 3
          }}
        >
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Edit Organization Details
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Update your organization information below
          </Typography>

          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid size={12}>
              <Typography variant="subtitle1" fontWeight={600} color="primary" gutterBottom>
                Basic Information
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Company Name"
                value={formData.company_name || ''}
                onChange={handleChange('company_name')}
                required
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Tagline"
                value={formData.tagline || ''}
                onChange={handleChange('tagline')}
                placeholder="Your company's motto or slogan"
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label="Company Type"
                value={formData.company_type || ''}
                onChange={handleChange('company_type')}
              >
                {companyTypes.map(type => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Industry Sector"
                value={formData.industry_sector || ''}
                onChange={handleChange('industry_sector')}
                placeholder="e.g., Technology, Healthcare"
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <DatePicker
                label="Founded Date"
                value={formData.founded_date ? dayjs(formData.founded_date) : null}
                onChange={handleDateChange}
                slotProps={{
                  textField: { fullWidth: true }
                }}
              />
            </Grid>

            {/* Registration Details */}
            <Grid size={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" fontWeight={600} color="primary" gutterBottom>
                Registration Details
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="GST Number"
                value={formData.gst_number || ''}
                onChange={handleChange('gst_number')}
                placeholder="e.g., 29ABCDE1234F1Z5"
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Registration Number (CIN)"
                value={formData.registration_number || ''}
                onChange={handleChange('registration_number')}
                placeholder="e.g., U74999KA2020PTC123456"
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="PAN Number"
                value={formData.pan_number || ''}
                onChange={handleChange('pan_number')}
                placeholder="e.g., ABCDE1234F"
              />
            </Grid>

            {/* Address */}
            <Grid size={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" fontWeight={600} color="primary" gutterBottom>
                Address
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Address Line 1"
                value={formData.address_line1 || ''}
                onChange={handleChange('address_line1')}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Address Line 2"
                value={formData.address_line2 || ''}
                onChange={handleChange('address_line2')}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="City"
                value={formData.city || ''}
                onChange={handleChange('city')}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="State"
                value={formData.state || ''}
                onChange={handleChange('state')}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Country"
                value={formData.country || 'India'}
                onChange={handleChange('country')}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Pincode"
                value={formData.pincode || ''}
                onChange={handleChange('pincode')}
              />
            </Grid>

            {/* Contact Information */}
            <Grid size={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" fontWeight={600} color="primary" gutterBottom>
                Contact Information
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email || ''}
                onChange={handleChange('email')}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone || ''}
                onChange={handleChange('phone')}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Alternate Phone"
                value={formData.alternate_phone || ''}
                onChange={handleChange('alternate_phone')}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Website"
                value={formData.website || ''}
                onChange={handleChange('website')}
                placeholder="https://www.example.com"
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="LinkedIn URL"
                value={formData.linkedin_url || ''}
                onChange={handleChange('linkedin_url')}
                placeholder="https://linkedin.com/company/..."
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Twitter URL"
                value={formData.twitter_url || ''}
                onChange={handleChange('twitter_url')}
                placeholder="https://twitter.com/..."
              />
            </Grid>
          </Grid>
        </Card>
      </Box>
    </LocalizationProvider>
  );
};

export default CompanyProfileSection;
